import { TreeKeyManagerItem } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { StateService } from '@uirouter/core';

import { CompanyTreeRow } from 'src/app/app-common/types/company-tree';
import { ComponentsModule } from 'src/app/components/components.module';
import { NO_ADDRESS_ON_RECORD } from 'src/app/shared/services/address-utils.service';

const INDENT_PER_LEVEL_PX = 32;

@Component({
  selector: 'a[company-tree-row]',
  templateUrl: './company-tree-row.component.html',
  styleUrl: './company-tree-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ComponentsModule, MatIconModule],
  hostDirectives: [MatTooltip],
  host: {
    class: 'company-tree-row',
    role: 'treeitem',
    '[class.company-tree-row--selected]': 'row().selected',
    '[attr.href]': 'href()',
    '[attr.tabindex]': 'isFocusable() ? 0 : -1',
    '[attr.aria-level]': 'row().level',
    '[attr.aria-posinset]': 'row().posInSet',
    '[attr.aria-setsize]': 'row().setSize',
    '[attr.aria-expanded]': 'ariaExpanded()',
    '[attr.aria-current]': 'row().selected ? "true" : null',
    '[attr.aria-label]': 'accessibleName()',
    '(click)': 'onClick($event)',
    '(keydown)': 'onKeydown($event)',
  },
})
export class CompanyTreeRowComponent
  implements AfterViewInit, TreeKeyManagerItem
{
  private readonly stateService = inject(StateService);
  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly tooltip = inject(MatTooltip);
  private readonly destroyRef = inject(DestroyRef);

  readonly row = input.required<CompanyTreeRow>();

  readonly selectCompany = output<string>();
  readonly toggleExpand = output<string>();
  readonly retry = output<string>();

  private readonly focusableState = signal(false);
  private parentRow: CompanyTreeRowComponent | null = null;
  private childRows: CompanyTreeRowComponent[] = [];

  protected readonly isFocusable = this.focusableState.asReadonly();

  protected readonly truncated = signal(false);

  protected readonly displayAddress = computed(
    () => this.row().address ?? NO_ADDRESS_ON_RECORD,
  );

  protected readonly showExpandControl = computed(() => {
    const row = this.row();
    return row.variant !== 'root' && row.expandable;
  });

  /**
   * Every level below the root reserves one indentation step, and the row's own
   * expand control sits in the last of them. Rows without a control pad that
   * step out instead, so a leaf stays aligned with its expandable siblings.
   */
  protected readonly indentWidth = computed(() => {
    const steps = Math.max(this.row().level - 1, 0);
    const control = this.showExpandControl() ? 1 : 0;
    return Math.max(steps - control, 0) * INDENT_PER_LEVEL_PX;
  });

  protected readonly showError = computed(() => !!this.row().errorMessage);

  protected readonly showSpinner = computed(
    () => this.row().loading && !this.showError(),
  );

  protected readonly showCheck = computed(
    () => this.row().selected && !this.showError() && !this.showSpinner(),
  );

  protected readonly showDot = computed(() => {
    const row = this.row();
    return (
      row.containsSelected &&
      !row.expanded &&
      !row.selected &&
      !this.showError() &&
      !this.showSpinner()
    );
  });

  protected readonly fullText = computed(
    () => `${this.row().name}, ${this.displayAddress()}`,
  );

  constructor() {
    effect(() => {
      const message = this.fullText();
      const disabled = !this.truncated();
      untracked(() => {
        this.tooltip.message = message;
        this.tooltip.disabled = disabled;
      });
    });

    effect(() => {
      this.row();
      untracked(() => {
        queueMicrotask(() => this.updateTruncated());
      });
    });
  }

  ngAfterViewInit(): void {
    this.updateTruncated();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => this.updateTruncated());
    observer.observe(this.hostEl.nativeElement);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  isLineTruncated(element: HTMLElement): boolean {
    return element.scrollWidth > element.clientWidth;
  }

  updateTruncated(): void {
    const root = this.hostEl.nativeElement;
    const name = root.querySelector<HTMLElement>('.company-tree-row__name');
    const address = root.querySelector<HTMLElement>(
      '.company-tree-row__address',
    );
    this.truncated.set(
      (!!name && this.isLineTruncated(name)) ||
        (!!address && this.isLineTruncated(address)),
    );
  }

  protected href(): string {
    const companyId = this.row().id;
    const current = this.stateService.current?.name;

    return current
      ? this.stateService.href(current, {
          ...this.stateService.params,
          cid: companyId,
        })
      : this.stateService.href('apps.home', { cid: companyId });
  }

  protected accessibleName(): string {
    const parts = [this.fullText()];

    if (this.row().selected) {
      parts.push('selected');
    }

    if (this.showDot()) {
      parts.push('contains selected company');
    }

    if (this.showError()) {
      parts.push('failed to load, press Alt+Enter to retry');
    }

    return parts.join(', ');
  }

  protected ariaExpanded(): boolean | null {
    const row = this.row();
    if (row.variant === 'root') {
      return true;
    }

    return row.expandable ? row.expanded : null;
  }

  protected onClick(event: MouseEvent): void {
    if (
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    this.selectCompany.emit(this.row().id);
  }

  protected onToggle(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.toggleExpand.emit(this.row().id);
  }

  setTreeLinks(
    parent: CompanyTreeRowComponent | null,
    children: CompanyTreeRowComponent[],
  ): void {
    this.parentRow = parent;
    this.childRows = children;
  }

  getParent(): TreeKeyManagerItem | null {
    return this.parentRow;
  }

  getChildren(): TreeKeyManagerItem[] {
    return this.childRows;
  }

  isExpanded = (): boolean =>
    this.row().variant === 'root' || this.row().expanded;

  getLabel(): string {
    return this.row().name;
  }

  activate(): void {
    this.selectCompany.emit(this.row().id);
  }

  expand(): void {
    const row = this.row();
    if (row.variant === 'root' || !row.expandable || row.expanded) {
      return;
    }

    this.toggleExpand.emit(row.id);
  }

  collapse(): void {
    const row = this.row();
    if (row.variant === 'root' || !row.expanded) {
      return;
    }

    this.toggleExpand.emit(row.id);
  }

  focus(): void {
    this.focusableState.set(true);
    const element = this.hostEl.nativeElement;
    const tree = element.closest('[role="tree"]');
    const active = element.ownerDocument.activeElement;
    if (tree && active && tree.contains(active)) {
      element.focus();
    }
  }

  unfocus(): void {
    this.focusableState.set(false);
  }

  makeFocusable(): void {
    this.focusableState.set(true);
  }

  protected onRetry(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.retry.emit(this.row().id);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (!this.showError() || event.key !== 'Enter' || !event.altKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.retry.emit(this.row().id);
  }
}
